import React, { useEffect, useState } from "react";
import MaterialTable from "material-table";
import { useSelector, useDispatch } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Avatar,
  Typography
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import { api } from "common";
import CircularLoading from "../components/CircularLoading";
import AlertDialog from "../components/AlertDialog";
import { ThemeProvider } from "@mui/material/styles";
import theme from "styles/tableStyle";
import TableStyle from "../components/Table/Style";
import localization from "../components/Table/Localization";
import { useTranslation } from "react-i18next";
import { SECONDORY_COLOR, FONT_FAMILY } from "../common/sharedFunctions";

const emptyBanner = {
  title: "",
  subtitle: "",
  image: "",
  active: true,
  audience: "customer",
  actionType: "none",
  actionValue: "",
  sortOrder: 0
};

export default function Banners() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const dispatch = useDispatch();
  const settings = useSelector(state => state.settingsdata.settings);
  const bannerdata = useSelector(state => state.bannerdata);
  const { editBanner } = api;
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(emptyBanner);
  const [alert, setAlert] = useState({ open: false, msg: "" });

  useEffect(() => {
    const banners = bannerdata.banners || [];
    setRows([...banners].sort((a, b) => (parseFloat(a.sortOrder || 0) - parseFloat(b.sortOrder || 0))));
  }, [bannerdata.banners]);

  const openForm = (banner) => {
    setData(banner ? { ...banner } : { ...emptyBanner, createdAt: new Date().getTime() });
    setOpen(true);
  };

  const closeForm = () => {
    setOpen(false);
    setSaving(false);
    setData(emptyBanner);
  };

  const handleChange = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!settings.AllowCriticalEditsAdmin) {
      setAlert({ open: true, msg: t("demo_mode") });
      return;
    }
    if (!data.title || (!data.image && !data.imageFile)) {
      setAlert({ open: true, msg: "Please add a title and banner image." });
      return;
    }

    setSaving(true);
    await dispatch(editBanner({ ...data, audience: "customer" }, data.id ? "Update" : "Add"));
    closeForm();
  };

  const columns = [
    {
      title: "Image",
      field: "image",
      render: rowData => rowData.image ?
        <Avatar src={rowData.image} variant="rounded" sx={{ width: 84, height: 44 }} /> :
        <Avatar variant="rounded"><ImageIcon /></Avatar>
    },
    { title: "Title", field: "title" },
    { title: "Subtitle", field: "subtitle" },
    { title: "Sort", field: "sortOrder", type: "numeric" },
    {
      title: "Active",
      field: "active",
      render: rowData => (
        <Switch
          checked={rowData.active === true}
          disabled={!settings.AllowCriticalEditsAdmin}
          onChange={() => dispatch(editBanner({ ...rowData, active: !rowData.active }, "Update"))}
        />
      )
    }
  ];

  return bannerdata.loading ? <CircularLoading /> : (
    <ThemeProvider theme={theme}>
      <MaterialTable
        title="Home Banners"
        columns={columns}
        data={rows}
        style={{
          direction: isRTL === "rtl" ? "rtl" : "ltr",
          borderRadius: "8px",
          boxShadow: `0px 2px 5px ${SECONDORY_COLOR}`,
          padding: "20px"
        }}
        options={TableStyle()}
        localization={localization(t)}
        editable={{
          onRowDelete: oldData =>
            settings.AllowCriticalEditsAdmin
              ? new Promise(resolve => {
                  setTimeout(() => {
                    resolve();
                    dispatch(editBanner(oldData, "Delete"));
                  }, 300);
                })
              : new Promise(resolve => {
                  resolve();
                  setAlert({ open: true, msg: t("demo_mode") });
                })
        }}
        actions={[
          {
            icon: "add",
            tooltip: "Add banner",
            isFreeAction: true,
            onClick: () => openForm()
          },
          rowData => ({
            icon: "edit",
            tooltip: "Edit banner",
            onClick: () => openForm(rowData)
          })
        ]}
      />

      <Dialog open={open} onClose={closeForm} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: FONT_FAMILY }}>{data.id ? "Edit Banner" : "Add Banner"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Title" value={data.title || ""} onChange={e => handleChange("title", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Subtitle" value={data.subtitle || ""} onChange={e => handleChange("subtitle", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Action</InputLabel>
                <Select label="Action" value={data.actionType || "none"} onChange={e => handleChange("actionType", e.target.value)}>
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="screen">Screen</MenuItem>
                  <MenuItem value="url">URL</MenuItem>
                  <MenuItem value="promo">Promo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Sort Order" type="number" value={data.sortOrder || 0} onChange={e => handleChange("sortOrder", parseFloat(e.target.value || 0))} />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField fullWidth label="Action Value" value={data.actionValue || ""} onChange={e => handleChange("actionValue", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Switch checked={data.active === true} onChange={e => handleChange("active", e.target.checked)} />} label="Active" />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label">
                Select Image
                <input hidden type="file" accept="image/*" onChange={e => handleChange("imageFile", e.target.files && e.target.files[0])} />
              </Button>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {data.imageFile ? data.imageFile.name : data.image ? "Current image will be kept unless replaced." : "No image selected."}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                Recommended banner size: 1600 x 700 px. Keep important text and logos in the center safe area.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
        </DialogActions>
      </Dialog>
      <AlertDialog open={alert.open} onClose={() => setAlert({ open: false, msg: "" })}>{alert.msg}</AlertDialog>
    </ThemeProvider>
  );
}
